import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Landmark, ShieldCheck, Copy, Check, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/finance`;

const CopyChip = ({ label, value }) => {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) { /* ignore */ }
  };
  return (
    <div className="flex items-center justify-between border rounded-lg px-3 py-2 bg-white gap-3">
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
        <div className="font-mono text-slate-800 truncate" data-testid={`pay-inst-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>{value}</div>
      </div>
      <button type="button" onClick={onCopy} className="text-slate-500 hover:text-slate-800 shrink-0" aria-label={`Copy ${label}`}>
        {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
      </button>
    </div>
  );
};

const GovPaymentInstructions = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/payment-instructions`);
        setData(r.data);
      } catch (e) {
        setErr(e?.response?.data?.detail || 'Failed to load payment instructions');
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
    );
  }

  if (err) {
    return (
      <Card className="border-red-200 bg-red-50" data-testid="pay-inst-error">
        <CardContent className="p-6 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
          <div>
            <p className="font-medium text-red-800">Can't display payment instructions</p>
            <p className="text-sm text-red-700 mt-1">{err}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="max-w-3xl space-y-6" data-testid="pay-inst-page">
      <div className="flex items-start gap-3">
        <Landmark className="h-6 w-6 text-blue-700 mt-0.5" />
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">Payment Instructions</h1>
          <p className="text-sm text-slate-500 max-w-xl mt-1">
            Confidential USKE Supply LLC banking details for ACH, Wire, and cheque payments.
            Do not forward, print, or share outside your finance team.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-md p-3">
        <ShieldCheck className="h-4 w-4 shrink-0" />
        {data.note}
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">ACH (Preferred · No fees)</CardTitle>
        </CardHeader>
        <CardContent className="grid sm:grid-cols-2 gap-3">
          <CopyChip label="Beneficiary" value={data.beneficiary} />
          <CopyChip label="Account Type" value={data.ach?.type} />
          <CopyChip label="Routing Number" value={data.ach?.routing || '— not configured —'} />
          <CopyChip label="Account Number" value={data.ach?.account || '— not configured —'} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Wire Transfer</CardTitle>
        </CardHeader>
        <CardContent className="grid sm:grid-cols-2 gap-3">
          <CopyChip label="Beneficiary" value={data.beneficiary} />
          <CopyChip label="Beneficiary Address" value={data.beneficiary_address} />
          <CopyChip label="Bank Name" value={data.wire?.bank_name || '— not configured —'} />
          <CopyChip label="Bank Address" value={data.wire?.bank_address || '— not configured —'} />
          <CopyChip label="Routing / ABA" value={data.wire?.routing || '— not configured —'} />
          <CopyChip label="Account Number" value={data.wire?.account || '— not configured —'} />
          <CopyChip label="SWIFT / BIC" value={data.wire?.swift || '— not configured —'} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Cheque</CardTitle>
        </CardHeader>
        <CardContent className="grid sm:grid-cols-2 gap-3">
          <CopyChip label="Payable To" value={data.cheque?.payable_to} />
          <CopyChip label="Mail To" value={data.cheque?.mail_to} />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4 text-sm text-slate-600 space-y-2">
          <p><b>Reference note:</b> {data.memo}</p>
          <p>
            Payment queries: <a className="text-blue-700 underline" href={`mailto:${data.support_email}`}>{data.support_email}</a>
            {data.support_phone && <> · {data.support_phone}</>}
          </p>
        </CardContent>
      </Card>

      <Button variant="outline" size="sm" onClick={() => {
        const flat = [
          `USKE Supply LLC — Payment Instructions`,
          `Beneficiary: ${data.beneficiary}`,
          `Beneficiary Address: ${data.beneficiary_address}`,
          `ACH Routing: ${data.ach?.routing || '-'}`,
          `ACH Account: ${data.ach?.account || '-'}`,
          `Wire Bank: ${data.wire?.bank_name || '-'}`,
          `Wire SWIFT: ${data.wire?.swift || '-'}`,
          `Cheque Payable To: ${data.cheque?.payable_to}`,
          `Cheque Mail To: ${data.cheque?.mail_to}`,
        ].join('\n');
        navigator.clipboard.writeText(flat).then(() => toast({ title: 'Copied all instructions to clipboard' }));
      }} data-testid="pay-inst-copy-all">
        <Copy className="h-4 w-4 mr-1" /> Copy all
      </Button>
    </div>
  );
};

export default GovPaymentInstructions;
